<template>
    <el-dialog v-model="dialogVisible" width="86%" top="30px" :show-close="false" draggable>
        <div class="header">
            <div class="title">历史委托</div>
            <div class="h-button">
                <el-button @click="load()" :loading="loading">刷新</el-button>
                <el-button @click="loadMore()" :loading="loadingMore" class="mr15">下一页 {{ page }}</el-button>
                <el-tag size="large" :type="pnl >= 0 ? 'success' : 'danger'" class="mr15">
                    实现盈亏 {{ new Decimal(pnl).toDecimalPlaces(4, Decimal.ROUND_DOWN).toNumber() }}</el-tag>
                <el-tag size="large" type="danger" class="mr15">
                    手续费 {{ new Decimal(fee).toDecimalPlaces(4, Decimal.ROUND_DOWN).toNumber() }}</el-tag>
                <el-tag size="large" :type="total >= 0 ? 'success' : 'danger'">
                    合计 {{ new Decimal(total).toDecimalPlaces(4, Decimal.ROUND_DOWN).toNumber() }}</el-tag>
            </div>
        </div>
        <div class="history-body">
            <el-table :data="list">
                <el-table-column type="index" width="80" align="center"></el-table-column>
                <el-table-column prop="symbol" label="交易对" align="center" width="150">
                    <template #default="{ row }">
                        <el-tag :type="row.positionSide === constants.SHORT ? 'danger' : 'success'"
                            style="width:120px; text-align:center">{{ row.symbol }}</el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="方向" align="center" width="80">
                    <template #default="{ row }">
                        {{ row.side === constants.SELL ? '平多' : '平空' }}
                    </template>
                </el-table-column>
                <el-table-column label="实现盈亏" align="center" width="120">
                    <template #default="{ row }">
                        <span :class="Number(row.realizedPnl) >= 0 ? 'green' : 'red'">{{ row.realizedPnl }}</span>
                    </template>
                </el-table-column>
                <el-table-column label="盈亏" align="center" width="120">
                    <template #default="{ row }">
                        <span :class="Number(row.realizedPnl1) >= 0 ? 'green' : 'red'">{{ row.realizedPnl1 }}</span>
                    </template>
                </el-table-column>
                <el-table-column prop="commission" label="手续费" align="center" width="110"></el-table-column>
                <el-table-column prop="quoteQty" label="成交额" align="center" width="110"></el-table-column>
                <el-table-column prop="qty" label="成交量" align="center" width="110"></el-table-column>
                <el-table-column prop="openTime" label="开仓时间" align="center" width="180"></el-table-column>
                <el-table-column prop="ctime" label="平仓时间" align="center" width="180"></el-table-column>
                <el-table-column label=""></el-table-column>
            </el-table>
        </div>
        <template #footer>
            <div class="dialog-footer">
                <el-button type="warning" size="large" @click="dialogVisible = false">关 闭</el-button>
            </div>
        </template>
    </el-dialog>
</template>
<style scoped>
.page {
    padding: 0 10px !important;
}

.header {
    display: flex;
    margin: 10px 0;
    padding-bottom: 10px;
    border-bottom: 1px solid #3a3a3a;
}

.history-body {
    height: calc(100vh - 250px);
    max-height: calc(100vh - 250px);
    overflow-y: auto;
}

.title {
    width: 100px;
    font-size: 20px;
    font-weight: bold;
    color: #3da1d3;
    line-height: 26px;
}

.green {
    color: #67c23a;
}

.red {
    color: #ff707e;
}

.mr15 {
    margin-right: 15px;
}
</style>

<script setup>
import { ref, toRaw, onMounted } from "vue"
import { ElMessage } from 'element-plus'
import { useSystemStore } from '@/stores/system'
import { useLogStore } from '@/stores/log'
import * as constants from '@/utils/binanceConstants'
import Decimal from 'decimal.js'
import dayjs from 'dayjs'

const systemStore = useSystemStore()
const logStore = useLogStore()
const loading = ref(false)
const loadingMore = ref(false)
const dialogVisible = ref(false)
const list = ref([])
const pnl = ref(0)
const fee = ref(0)
const total = ref(0)
const page = ref(1)
const fetchData = ref([])

let currentEndTime = null

onMounted(() => {
    load()
})

const showDialog = () => {
    dialogVisible.value = true
}

const load = async () => {
    try {
        loading.value = true
        fee.value = 0
        pnl.value = 0
        total.value = 0
        list.value = []
        page.value = 1
        fetchData.value = []

        const params = {
            limit: 1000
        }

        let result = await systemStore.privateClient.myTrades(params)
        fetchData.value = result
        currentEndTime = Date.now()
        compute()
        loading.value = false

    } catch (error) {
        ElMessage.error(`请求异常： ${error.message}`)
        loading.value = false
        logStore.addLog({
            title: '获取历史委托失败',
            level: 'error',
            message: error
        })
    }
}

const loadMore = async () => {
    try {
        loadingMore.value = true

        if (!currentEndTime) return

        const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000
        const endTime = Number(currentEndTime) - SEVEN_DAYS
        const startTime = Number(endTime - SEVEN_DAYS)
        const params = {
            startTime,
            endTime,
            limit: 1000
        }

        let result = await systemStore.privateClient.myTrades(params)
        fetchData.value.push(...result)
        currentEndTime = endTime
        compute()
        loadingMore.value = false
        page.value++

    } catch (error) {
        ElMessage.error(`请求异常： ${error.message}`)
        loadingMore.value = false
        logStore.addLog({
            title: '获取历史委托失败',
            level: 'error',
            message: error
        })
    }
}

const getGroup = (data) => {
    let map = new Map()
    data.forEach((item) => {
        let key = item.symbol + item.side + item.time
        if (!map.has(key)) {
            map.set(
                key,
                data.filter(a => a.symbol == item.symbol && a.side == item.side && a.time == item.time)
            )
        }
    })
    return map
}

// 配对开仓和平仓，获得开仓时间
const matchOpenAndClose = (allData) => {
    // 创建一个map来存储每个symbol+positionSide的开仓堆栈
    const openStacks = new Map()
    // 创建一个map来存储平仓对应的开仓时间
    const closeToOpenMap = new Map()

    // 按symbol和positionSide分组，然后按时间排序
    const grouped = new Map()
    allData.forEach(trade => {
        const key = `${trade.symbol}_${trade.positionSide}`
        if (!grouped.has(key)) {
            grouped.set(key, [])
        }
        grouped.get(key).push(trade)
    })

    // 对每个组进行FIFO匹配
    for (const [key, trades] of grouped) {
        const sorted = trades.sort((a, b) => Number(a.time) - Number(b.time))
        const stack = []

        for (const trade of sorted) {
            const isOpen = trade.realizedPnl === "0" || trade.realizedPnl === 0

            if (isOpen) {
                // 开仓：入栈
                stack.push(trade)
            } else {
                // 平仓：出栈并配对
                const closeQty = new Decimal(trade.qty)
                let remaining = closeQty

                while (remaining.gt(0) && stack.length > 0) {
                    const openTrade = stack[0]
                    const openQty = new Decimal(openTrade.qty)

                    if (remaining.gte(openQty)) {
                        // 平仓量>=开仓量，说明开仓被完全平掉
                        remaining = remaining.minus(openQty)
                        stack.shift()

                        // 创建key来标记这对交易
                        const tradeKey = `${openTrade.id}_${trade.id}`
                        closeToOpenMap.set(tradeKey, openTrade)
                    } else {
                        // 平仓量<开仓量，说明只平掉一部分
                        stack[0] = {
                            ...openTrade,
                            qty: openQty.minus(remaining).toString()
                        }
                        remaining = new Decimal(0)

                        const tradeKey = `${openTrade.id}_${trade.id}`
                        closeToOpenMap.set(tradeKey, openTrade)
                    }
                }
            }
        }
    }

    return closeToOpenMap
}

const compute = () => {
    if (fetchData.value.length == 0) return

    let result = fetchData.value.filter(x => x.realizedPnl !== "0").sort((a, b) => {
        return b.time - a.time
    })

    // 获得开仓和平仓的对应关系
    const closeToOpenMap = matchOpenAndClose(fetchData.value)

    //手续费(开仓也要算进去)
    fee.value = fetchData.value.reduce((sum, item) => sum + Number(item.commission), 0)
    //盈利(只计算平仓的)
    pnl.value = result.reduce((sum, item) => sum + Number(item.realizedPnl), 0)
    //总计
    total.value = (pnl.value - fee.value)

    //获取分组
    const groupData = getGroup(result)

    const resultList = []

    for (const [, records] of groupData) {
        let realizedPnl = new Decimal(0)
        let commission = new Decimal(0)
        let quoteQty = new Decimal(0)
        let qty = new Decimal(0)
        let openTime = null

        for (const record of records) {
            realizedPnl = realizedPnl.plus(record.realizedPnl)
            quoteQty = quoteQty.plus(record.quoteQty)
            //手续费直接用成交量 * 0.001来计算，避免有些成交记录的手续费为0的情况
            commission = quoteQty.times(0.001)
            qty = qty.plus(record.qty)

            // 查找对应的开仓时间
            if (!openTime) {
                for (const [tradeKey, openTrade] of closeToOpenMap) {
                    if (tradeKey.endsWith(`_${record.id}`)) {
                        openTime = dayjs(parseInt(openTrade.time)).format("YYYY-MM-DD HH:mm:ss")
                        break
                    }
                }
            }
        }

        let item = {
            symbol: records[0].symbol,
            side: records[0].side,
            positionSide: records[0].positionSide,
            openTime: openTime || '-',  // 新增：开仓时间
            ctime: dayjs(parseInt(records[0].time)).format("YYYY-MM-DD HH:mm:ss"),  // 平仓时间
            realizedPnl: realizedPnl.minus(commission),
            realizedPnl1: realizedPnl,
            commission: commission,
            quoteQty: quoteQty,
            qty: qty,
        }

        resultList.push(item)
    }

    list.value = resultList
}

defineExpose({ showDialog })

</script>
